import createApp from "@shopify/app-bridge";
import { getSessionToken } from "@shopify/app-bridge-utils";

/**
 * App Bridge Initialization
 * 
 * Phase 4.1.1: Real implementation for embedded admin app
 * 
 * This module initializes Shopify App Bridge and provides
 * utilities for session token retrieval.
 */

let appInstance: ReturnType<typeof createApp> | null = null;

// Cache URL parameters to preserve them across navigation
let cachedShop: string | null = null;
let cachedHost: string | null = null;

/** Same-tab persistence when Shopify / router drops `shop`/`host` from the URL (e.g. browser back). */
const STORAGE_SHOP_KEY = "beyondgames:shopify:shop";
const STORAGE_HOST_KEY = "beyondgames:shopify:host";

export function readPersistedEmbeddedParams(): {
  shop: string | null;
  host: string | null;
} {
  if (typeof sessionStorage === "undefined") {
    return { shop: null, host: null };
  }
  return {
    shop: sessionStorage.getItem(STORAGE_SHOP_KEY),
    host: sessionStorage.getItem(STORAGE_HOST_KEY),
  };
}

function persistEmbeddedParams(shop: string, host: string | null): void {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  sessionStorage.setItem(STORAGE_SHOP_KEY, shop);
  if (host) {
    sessionStorage.setItem(STORAGE_HOST_KEY, host);
  }
}

/** When the URL still has params, mirror them into sessionStorage for reload / history restore. */
function persistEmbeddedParamsFromCurrentUrl(): void {
  const params = new URLSearchParams(window.location.search);
  const shop = params.get("shop");
  const host = normalizeShopifyHostParam(params.get("host"));
  if (shop) {
    persistEmbeddedParams(shop, host);
  }
}

/**
 * Normalize the Shopify `host` URL param for App Bridge and admin API usage.
 * Rejects empty values and literal "null"/"undefined" strings (truthy in JS, invalid for Shopify).
 */
export function normalizeShopifyHostParam(
  host: string | null | undefined
): string | null {
  if (host == null) {
    return null;
  }
  const trimmed = host.trim();
  if (!trimmed) {
    return null;
  }
  const lower = trimmed.toLowerCase();
  if (lower === "null" || lower === "undefined") {
    return null;
  }
  return trimmed;
}

/**
 * Initialize Shopify App Bridge
 * 
 * Should be called once when the app mounts
 * 
 * @returns App Bridge instance
 */
export function initializeAppBridge(): ReturnType<typeof createApp> {
  if (appInstance) {
    return appInstance;
  }

  persistEmbeddedParamsFromCurrentUrl();

  // Get shop and host from URL params (provided by Shopify), then same-tab storage if stripped (back nav / reload).
  const urlParams = new URLSearchParams(window.location.search);
  let shop = urlParams.get("shop");
  let hostNorm = normalizeShopifyHostParam(urlParams.get("host"));

  if (!shop || !hostNorm) {
    const persisted = readPersistedEmbeddedParams();
    if (!shop) {
      shop = persisted.shop;
    }
    if (!hostNorm && persisted.host) {
      hostNorm = normalizeShopifyHostParam(persisted.host);
    }
  }

  if (!shop) {
    throw new Error("Missing required parameter: shop");
  }

  // Cache URL parameters for future use (persists across navigation)
  cachedShop = shop;
  cachedHost = hostNorm;

  persistEmbeddedParams(shop, cachedHost);

  // API key should be in environment variable
  const apiKey = (import.meta as { env?: { VITE_SHOPIFY_API_KEY?: string } }).env?.VITE_SHOPIFY_API_KEY;

  if (!apiKey) {
    throw new Error("Missing VITE_SHOPIFY_API_KEY environment variable");
  }

  appInstance = createApp({
    apiKey,
    host: cachedHost || "",
    forceRedirect: true,
  });

  // Expose utilities globally for debugging
  const shopifyAppUtils = {
    app: appInstance,
    getSessionToken: getShopifySessionToken,
    getShopifySessionToken: getShopifySessionToken,
    isEmbedded: isEmbedded,
    initializeAppBridge: initializeAppBridge,
    getCachedShop: getCachedShop,
    getCachedHost: getCachedHost,
  };
  
  (window as any).shopifyApp = shopifyAppUtils;

  console.log("[App Bridge] Initialized successfully");
  console.log("[App Bridge] Shop:", shop);
  console.log("[App Bridge] Host:", cachedHost ? "present" : "missing");
  console.log("[App Bridge] Use window.shopifyApp.getSessionToken() to get token");
  console.log("[App Bridge] Utils exposed:", Object.keys(shopifyAppUtils).join(', '));

  return appInstance;
}

/**
 * Get current App Bridge instance
 * 
 * @returns App Bridge instance or null if not initialized
 */
export function getAppBridgeInstance(): ReturnType<typeof createApp> | null {
  return appInstance;
}

/**
 * Get Shopify session token
 * 
 * This token is a JWT signed by Shopify that can be used
 * to authenticate admin API requests.
 * 
 * @returns Session token (JWT)
 */
export async function getShopifySessionToken(): Promise<string> {
  const app = getAppBridgeInstance();

  if (!app) {
    throw new Error("App Bridge not initialized. Call initializeAppBridge() first.");
  }

  try {
    const token = await getSessionToken(app);
    return token;
  } catch (error) {
    console.error("Failed to get session token:", error);
    throw new Error("Failed to get session token from Shopify");
  }
}

/**
 * Check if running in embedded Shopify admin
 *
 * Uses cached parameters to work across navigation.
 * In real admin, client-side navigation often drops `host` from the query string while
 * the app remains inside the Shopify iframe — require shop + (host OR iframe OR prior init).
 */
export function isEmbedded(): boolean {
  persistEmbeddedParamsFromCurrentUrl();

  if (cachedShop && cachedHost) {
    return true;
  }
  if (cachedShop && appInstance) {
    return true;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const shop = urlParams.get("shop");
  const hostNorm = normalizeShopifyHostParam(urlParams.get("host"));
  if (shop && hostNorm) {
    return true;
  }

  const inIframe =
    typeof window !== "undefined" && window.self !== window.top;
  if (shop && inIframe) {
    return true;
  }

  if (urlParams.has("shop") && urlParams.has("host")) {
    return true;
  }

  // Same-tab embedded session after URL params were dropped (browser history / iframe reload).
  const persisted = readPersistedEmbeddedParams();
  const pShop = persisted.shop;
  const pHost = normalizeShopifyHostParam(persisted.host);
  if (pShop && pHost) {
    return true;
  }
  if (pShop && inIframe) {
    return true;
  }

  return false;
}

/**
 * Get cached shop parameter
 * 
 * @returns Shop domain or null if not initialized
 */
export function getCachedShop(): string | null {
  return cachedShop;
}

/**
 * Get cached Shopify admin `host` for API calls (base64 host param).
 *
 * @returns Non-empty host string, or null if missing / invalid (e.g. literal "null" from URL)
 */
export function getCachedHost(): string | null {
  return normalizeShopifyHostParam(cachedHost);
}
